import { verificationTab } from "StaticData/OrganizationDetail";

export type RefId = string | number;
export type FiltersType = {
  page?: number;
  pageSize?: number;
  search?: string;
  region?: string;
};

export const BASE_URL = process.env.REACT_APP_REST_API_URL;
export const SSO_API_URL = process.env.REACT_APP_SSO_URL + "api/sso/";
export const MAIN_APP_URL = process.env.REACT_APP_BASE_URL;
export const LOGIN_PAGE = `${SSO_API_URL}users/login/`;
export const LOGOUT_PAGE = `${SSO_API_URL}users/logout/`;
export const CHOOSE_PLATFORM_PAGE = `${SSO_API_URL}platforms/choose/`;
const GOOGLE_API_BASE_URL = "https://maps.googleapis.com/maps/api";

export const SsoApiUrl = {
  tokenVerify: `${SSO_API_URL}jwt/token/verify/`,
  tokenRefresh: `${SSO_API_URL}jwt/token/refresh/`,
  passwordUpdate: `${SSO_API_URL}accounts/password_change/`,
  featureFlag: `${SSO_API_URL}feature-flags/`,
  platformsManagement: {
    platforms: `${SSO_API_URL}platforms/`,
    platform: (code: RefId) => `${SSO_API_URL}platforms/${code}/`,
  },

  organization: {
    organizationInviteList: `${SSO_API_URL}organizations/invite-list/`,
    organizationCurrent: `${SSO_API_URL}organizations/me/`,
    organizations: `${SSO_API_URL}organizations/`,
    organization: (ref: RefId) => `${SSO_API_URL}organizations/${ref}/`,
    organizationRoles: `${SSO_API_URL}organizations/roles/`,
    organizationUsers: (ref: RefId) =>
      `${SSO_API_URL}organizations/${ref}/users/`,
    organizationUser: (ref: RefId, userRef: RefId) =>
      `${SSO_API_URL}organizations/${ref}/users/${userRef}/`,
    organizationUserRole: (ref: RefId, userRef: RefId, roleId: RefId) =>
      `${SSO_API_URL}organizations/${ref}/users/${userRef}/assign-role/${roleId}/`,
  },
  roles: {
    roles: `${SSO_API_URL}roles/`,
    rolesPermissions: `${SSO_API_URL}roles/permissions/`,
    rolesPlatforms: `${SSO_API_URL}roles/platform-roles/`,
    role: (id: RefId) => `${SSO_API_URL}roles/${id}/`,
    rolesPlatform: (id: RefId) => `${SSO_API_URL}roles/platform-roles/${id}/`,
  },
  userManagement: {
    users: `${SSO_API_URL}users/`,
    user: (ref: RefId) => `${SSO_API_URL}users/${ref}/`,
    userCurrent: `${SSO_API_URL}users/me/`,
    userProjects: (ref: RefId) => `${SSO_API_URL}users/${ref}/projects/`,
    usersInvite: `${SSO_API_URL}users/invite/`,
    userInvite: (ref: RefId) => `${SSO_API_URL}users/invite/${ref}/`,
    userStatusOptions: `${SSO_API_URL}users/status-count/`,
  },
  projectManagement: {
    projects: `${SSO_API_URL}projects/`,
    project: (ref: RefId) => `${SSO_API_URL}projects/${ref}/`,
    projectUsers: (ref: RefId) => `${SSO_API_URL}projects/${ref}/members/`,
    projectUser: (ref: RefId, userRef: RefId) =>
      `${SSO_API_URL}projects/${ref}/members/${userRef}/`,
    projectUserRole: (ref: RefId, userRef: RefId, roleId: RefId) =>
      `${SSO_API_URL}projects/${ref}/members/${userRef}/assign-role/${roleId}/`,
    projectRegions: `${SSO_API_URL}projects/regions/`,
    projectRegion: (ref: RefId) => `${SSO_API_URL}projects/regions/${ref}/`,
  },
  filterOptions: {
    projectStatusCount: `${SSO_API_URL}projects/status-count/`,
    projectUsersCount: `${SSO_API_URL}projects/members-count/`,
    organizationUsersCount: `${SSO_API_URL}organizations/users-count/`,
    organizationProjectsCount: `${SSO_API_URL}organizations/projects-count/`,
    accountManagersCount: `${SSO_API_URL}users/account-managers/`,
    usersStatusCount: `${SSO_API_URL}users/status-count/`,
  },
  webhooks: {
    webhooks: `${SSO_API_URL}webhooks/`,
    events: `${SSO_API_URL}webhooks/events/`,
    webhook: (id: RefId) => `${SSO_API_URL}webhooks/${id}/`,
    webhookRequests: (id: RefId) => `${SSO_API_URL}webhooks/${id}/requests/`,
    webhookRequest: (id: RefId, requestId: RefId) =>
      `${SSO_API_URL}webhooks/${id}/request/${requestId}/`,
    webhookRequestRetry: (id: RefId, requestId: RefId) =>
      `${SSO_API_URL}webhooks/${id}/request/${requestId}/retry/`,
  },
  globalVariables: {
    globalVariables: `${SSO_API_URL}global-variables/`,
    globalVariable: (name: string) => `${SSO_API_URL}global-variables/${name}/`,
    anthropicModels: `${SSO_API_URL}global-variables/anthropic-models/`,
  },
};

export const ApiUrl = {
  BASE_URL,
  profileModule: {
    userMinimal: `${BASE_URL}users/minimal/`,
    userPreferences: `${BASE_URL}users/mypreference/`,
  },
  auditlog: {
    projects: `${BASE_URL}auditlog/project-list/`,
    users: `${BASE_URL}auditlog/user-list/`,
    organizations: `${BASE_URL}auditlog/organization-list/`,
  },
  project: {
    sites: `${BASE_URL}sites/`,
    siteContacts: (id: RefId) => `${BASE_URL}sites/${id}/contacts/`,
    siteContact: (id: RefId, contactId: RefId) =>
      `${BASE_URL}sites/${id}/contacts/${contactId}/`,
    legacyTime: `${BASE_URL}legacy/time/`,
    bookingFields: `${BASE_URL}booking-fields/`,
    bookingFieldsForms: `${BASE_URL}booking-fields/forms/`,
    bookingFieldsForm: (id: RefId) => `${BASE_URL}booking-fields/forms/${id}/`,
    bookingFieldsVehicles: `${BASE_URL}booking-fields/vehicle-type/`,
    vehicleTypes: `${BASE_URL}projects/vehicle-type/`,
    bookingFieldsVehicle: (id: RefId) =>
      `${BASE_URL}booking-fields/vehicle-type/${id}/`,
    proFeature: `${BASE_URL}projects/pro-features/`,
    integrations: `${BASE_URL}integrations/`,
    settings: `${BASE_URL}projects/settings/`,
  },
  googleAPI: {
    getGeoCode: `${GOOGLE_API_BASE_URL}/geocode/json`,
    getTimeZone: `${GOOGLE_API_BASE_URL}/timezone/json`,
  },
  preferences: {
    languages: `${BASE_URL}preferences/language/`,
    dateFormats: `${BASE_URL}preferences/date/format/`,
    timeFormats: `${BASE_URL}preferences/time/format/`,
    calenderDays: `${BASE_URL}preferences/calender/days/`,
  },
  commonUtils: {
    getCommonUtilsByType: (type: string) =>
      `${BASE_URL}common-utils/common-utils/filter/?type=${type}`,
  },
  equipmentModule: {
    equipmentApprovelData: `${BASE_URL}equipments/site-equipment-master/list-global-approval/`,
    equipmentApprovel: `${BASE_URL}equipments/site-equipment-master/approve-global-equipment/`,
    equipmentReject: `${BASE_URL}equipments/site-equipment-master/reject-global-equipment/`,
    equipmentMasterData: `${BASE_URL}equipments/site-equipment-master/list_global_equipments/`,
    equipmentMasterCopy: `${BASE_URL}equipments/site-equipment-master/copy-from-global/`,
    equipmentMaster: `${BASE_URL}equipments/equipment-master/`,
    equipmentwithsite: `${BASE_URL}equipments/site-equipment-master/`,
    equipmentMasterById: (ref: RefId) =>
      `${BASE_URL}equipments/equipment-master/${ref}/`,
    equipmentSiteById: (ref: RefId) =>
      `${BASE_URL}equipments/site-equipment-master/${ref}/`,
    equpimentTypes: `${BASE_URL}equipments/equipment-master/equipment_types/`,
    equpimentCategories: `${BASE_URL}equipments/equipment-master/categories/`,
    equipmentBrads: `${BASE_URL}equipments/equipment-master/brands/`,
    equipmentRegistry: `${BASE_URL}equipments/equipment-registry/`,
    getListOfEquipmentRegistry: `${BASE_URL}equipments/equipment-registry/unique-equipment/`,
    equipmentRegistryBulkUpload: `${BASE_URL}equipments/equipment-registry/bulk-upload/`,
    equipmentRegistryById: (ref: RefId) =>
      `${BASE_URL}equipments/equipment-registry/${ref}/`,
    equipmentMonthlyUsage: `${BASE_URL}equipments/monthly-usage/`,
    equipmentMonthlyUsageById: (ref: RefId) =>
      `${BASE_URL}equipments/monthly-usage/${ref}/`,
    downloadEquipmentUsageReports: `${BASE_URL}equipments/monthly-usage/export/`,
    uploadEquipmentUsage: `${BASE_URL}equipments/monthly-usage/upload_equipment_usage/`,
    uploadEquipment: `${BASE_URL}equipments/equipment-master/bulk_upload/`,
    equipmentUsageAttachment: (ref: RefId) =>
      `${BASE_URL}equipments/equipment-usage/${ref}/attachments/`,
    offRoadReports: `${BASE_URL}equipments/equipment-reports/usage_off_road_report/`,
    materialTransportReports: `${BASE_URL}equipments/equipment-reports/material_transport_report/`,
    summaryReports: `${BASE_URL}equipments/equipment-reports/usage_summary_report/`,
    environmentReports: `${BASE_URL}equipments/equipment-reports/chart_and_table_data/`,
    downloadOffRoadReports: `${BASE_URL}equipments/equipment-reports/export_report/`,
    downloadEquipmentListReports: `${BASE_URL}equipments/equipment-registry/export/`,
  },
  vehicleModule: {
    vehicleMasterData: `${BASE_URL}vehicles/site-vehicle-emissions/list_global_vehicles/`,
    vehicleApprovel: `${BASE_URL}vehicles/site-vehicle-emissions/approve_global_vehicle/`,
    vehicleReject: `${BASE_URL}vehicles/site-vehicle-emissions/reject-global-vehicle/`,
    vehicleTypes: `${BASE_URL}projects/vehicle-type/`,
    VehicleMasterCopy: `${BASE_URL}vehicles/site-vehicle-emissions/copy-from-global/`,
    vehicleMaster: `${BASE_URL}vehicles/vehicle-master/`,
    vehicleApprovaldata: `${BASE_URL}vehicles/site-vehicle-emissions/list-global-approval/`,
    vehicleSiteData: `${BASE_URL}vehicles/site-vehicle-emissions/`,
    vehicleMasterById: (ref: RefId) =>
      `${BASE_URL}vehicles/vehicle-master/${ref}/`,
    vehicleEmission: `${BASE_URL}vehicles/vehicle-emissions/`,
    vehicleEmissionById: (ref: RefId) =>
      `${BASE_URL}vehicles/vehicle-emissions/${ref}/`,
    vehicleSiteEmissionById: (ref: RefId) =>
      `${BASE_URL}vehicles/site-vehicle-emissions/${ref}/`,
    uniqueVehicles: `${BASE_URL}vehicles/vehicle-master/unique-vehicles/`,
  },
  systemBanners: {
    banners: `${BASE_URL}system-banners/`,
    banner: (id: RefId) => `${BASE_URL}system-banners/${id}/`,
  },
};
